import { useState, useEffect } from 'react';
import {
  BuildingOffice2Icon,
  PlusIcon,
  TrashIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  BoltIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { mspAPI } from '../../services/api';

export default function MSPPage() {
  const { user, loginWithToken } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await mspAPI.getDashboard();
      setDashboard(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load MSP dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableMsp = async () => {
    try {
      setActionLoading('enable');
      await mspAPI.enable();
      setSuccess('MSP mode enabled');
      await fetchDashboard();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to enable MSP mode');
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async (clientId, clientName) => {
    if (!confirm(`Switch to ${clientName}'s workspace? You'll have full access for 1 hour.`)) return;

    try {
      setActionLoading(`impersonate-${clientId}`);
      const response = await mspAPI.impersonate(clientId);
      const { token } = response.data.data;

      // Store original token for returning
      localStorage.setItem('msp_original_token', localStorage.getItem('token'));

      // Login with new token and redirect to dashboard
      if (loginWithToken) {
        const result = await loginWithToken(token);
        if (result.success) {
          window.location.href = '/dashboard';
        } else {
          setError(result.error || 'Failed to switch to client workspace');
          setActionLoading(null);
        }
      } else {
        localStorage.setItem('token', token);
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to impersonate client');
      setActionLoading(null);
    }
  };

  const handleRemoveClient = async (clientId) => {
    if (!confirm('Remove this client from your MSP portal? They will retain their data but lose enterprise features.')) return;

    try {
      setActionLoading(`remove-${clientId}`);
      await mspAPI.removeClient(clientId);
      setSuccess('Client removed');
      await fetchDashboard();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove client');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkRemediate = async (mode) => {
    if (mode === 'apply' && !confirm(
      'Apply security fixes across ALL client workspaces? This will make real changes to each client tenant.'
    )) return;

    try {
      setActionLoading(`bulk-${mode}`);
      setError(null);
      setBulkResult(null);
      const response = await mspAPI.bulkRemediate(mode);
      const data = response.data.data;
      setBulkResult(data);
      setSuccess(
        `Bulk ${mode} ran across ${data.clientCount} client(s).`
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to run bulk remediation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadReport = (clientId) => {
    mspAPI.downloadClientReport(clientId);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Not MSP mode - show enable option
  if (!dashboard?.isMsp) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <BuildingOffice2Icon className="mx-auto h-16 w-16 text-gray-400" />
          <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">
            MSP Portal
          </h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Manage multiple client workspaces from a single dashboard. Create client tenants, view aggregated stats, and access client workspaces with a click.
          </p>
          <button
            onClick={handleEnableMsp}
            disabled={actionLoading === 'enable'}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {actionLoading === 'enable' ? 'Enabling...' : 'Enable MSP Mode'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">MSP Portal</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your client workspaces
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleBulkRemediate('preview')}
            disabled={actionLoading?.startsWith('bulk') || dashboard.clients.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-700 dark:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50"
            title="Preview fixes across all client workspaces"
          >
            <BoltIcon className="h-5 w-5" />
            {actionLoading === 'bulk-preview' ? 'Previewing...' : 'Preview all clients'}
          </button>
          <button
            onClick={() => handleBulkRemediate('apply')}
            disabled={actionLoading?.startsWith('bulk') || dashboard.clients.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
            title="Apply fixes across all client workspaces"
          >
            <BoltIcon className="h-5 w-5" />
            {actionLoading === 'bulk-apply' ? 'Applying...' : 'Run across all clients'}
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Add Client
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
          </div>
        </div>
      )}

      {/* Bulk remediation result */}
      {bulkResult && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            Bulk {bulkResult.mode} results ({bulkResult.clientCount} client{bulkResult.clientCount === 1 ? '' : 's'})
          </h3>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {bulkResult.results.map((r) => (
              <li key={r.tenantId} className="py-2 flex items-center justify-between text-sm">
                <span className="text-gray-900 dark:text-white">{r.tenantName}</span>
                {r.error ? (
                  <span className="text-red-600 dark:text-red-400">{r.error}</span>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400">
                    {r.summary?.total ?? 0} action(s) · job #{r.jobId}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Aggregated Stats */}
      {dashboard.stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <BuildingOffice2Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {dashboard.stats.clientCount}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Clients</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <UserGroupIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {dashboard.stats.totalUsers}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Users</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <ShieldCheckIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {dashboard.stats.mfaStats.enabled}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">MFA Enabled</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {dashboard.stats.alertCount}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Active Alerts</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client List */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Clients</h2>
        </div>

        {dashboard.clients.length === 0 ? (
          <div className="p-8 text-center">
            <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No clients yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add your first client to get started.
            </p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Add Client
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Added
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {dashboard.clients.map((client) => (
                <tr key={client.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
                          {client.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {client.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {client.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {client.owner ? (
                      <div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {client.owner.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {client.owner.email}
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">No owner</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      client.status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(client.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleImpersonate(client.id, client.name)}
                        disabled={actionLoading === `impersonate-${client.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors disabled:opacity-50"
                        title="Access client workspace"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Access
                      </button>
                      <button
                        onClick={() => handleDownloadReport(client.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="Download Cyber Essentials report"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        Report
                      </button>
                      <button
                        onClick={() => handleRemoveClient(client.id)}
                        disabled={actionLoading === `remove-${client.id}`}
                        className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                        title="Remove client"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Client Modal */}
      {isCreateModalOpen && (
        <CreateClientModal
          onClose={() => setIsCreateModalOpen(false)}
          onCreated={() => {
            setIsCreateModalOpen(false);
            setSuccess('Client created successfully');
            fetchDashboard();
          }}
        />
      )}
    </div>
  );
}

function CreateClientModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter a company name');
      return;
    }

    if (!ownerEmail.trim()) {
      setError('Please enter an owner email');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await mspAPI.createClient({
        name: name.trim(),
        ownerEmail: ownerEmail.trim(),
        ownerName: ownerName.trim() || undefined,
      });

      onCreated();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Add New Client
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Corp"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Owner Email *
              </label>
              <input
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="owner@acme.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                If this email doesn't exist, a new account will be created
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Owner Name
              </label>
              <input
                type="text"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder="John Smith"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
